import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import {
  Users, Calendar, HeartPulse, PoundSterling, Sparkles, User,
  UserPlus, MailCheck, CalendarCheck, CreditCard, Repeat, AlertTriangle,
  Activity, Clock, TrendingUp, Info,
} from "lucide-react";

const ORANGE = "#f47a20";

function fmtGBP(n) {
  if (n == null) return "—";
  return "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function pct(n) {
  return n == null ? "—" : `${n}%`;
}
function humanize(s) {
  return String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// --- Lightweight inline SVG bar chart (no dependencies) ----------------------
function BarChart({ data, valueKey, labelKey, format = (v) => v, color = ORANGE, testid }) {
  if (!data || data.length === 0) {
    return <div className="text-sm py-8 text-center" style={{ color: "var(--fl-muted-2)" }} data-testid={`${testid}-empty`}>No data in range</div>;
  }
  const max = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  const show = data.slice(-30);
  return (
    <div className="flex items-end gap-1 h-36 w-full" data-testid={testid}>
      {show.map((d, i) => {
        const v = Number(d[valueKey]) || 0;
        const h = Math.max((v / max) * 100, v > 0 ? 4 : 1);
        return (
          <div key={i} className="group relative flex-1 max-w-[44px] flex flex-col justify-end items-center h-full" title={`${d[labelKey]}: ${format(v)}`}>
            <div
              className="w-full rounded-t transition-all duration-300"
              style={{ height: `${h}%`, background: `linear-gradient(to top, ${color}, ${color}aa)`, minHeight: 2 }}
            />
          </div>
        );
      })}
    </div>
  );
}

// Horizontal bars (revenue by package / followups by status)
function HBars({ rows, color = ORANGE, format = (v) => v, testid }) {
  if (!rows || rows.length === 0) {
    return <div className="text-sm py-6 text-center" style={{ color: "var(--fl-muted-2)" }} data-testid={`${testid}-empty`}>Not tracked</div>;
  }
  const max = Math.max(...rows.map((r) => Number(r.value) || 0), 1);
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex justify-between text-sm mb-1">
            <span className="truncate" style={{ color: "var(--fl-text-2)" }}>{r.label}</span>
            <span style={{ color: "var(--fl-muted)" }}>{format(r.value)}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--fl-bg-3)" }}>
            <div className="h-full rounded-full" style={{ width: `${(Number(r.value) / max) * 100}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, iconCls = "text-[#f47a20]", testid }) {
  return (
    <div className="fl-card p-5" data-testid={testid}>
      <Icon className={iconCls} size={18} />
      <div className="fl-label mt-3">{label}</div>
      <div className="fl-heading text-3xl mt-1">{value ?? "—"}</div>
      {sub != null && <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="fl-label text-[#f47a20] mt-10 mb-3">{children}</h2>;
}

export default function AdminHome() {
  const { t } = useTranslation();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [clinician, setClinician] = useState("");
  const reqRef = useRef(0);

  const load = () => {
    const rid = ++reqRef.current;
    setLoading(true);
    setErr(false);
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    if (clinician) params.clinician_id = clinician;
    api.get("/admin/analytics", { params })
      .then((r) => { if (rid === reqRef.current) setA(r.data); })
      .catch(() => { if (rid === reqRef.current) setErr(true); })
      .finally(() => { if (rid === reqRef.current) setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, clinician]);

  const acq = a?.acquisition || {};
  const ret = a?.retention || {};
  const rev = a?.revenue || {};
  const util = a?.utilisation || [];
  const clinicians = a?.clinicians || [];

  const fuRows = useMemo(
    () => Object.entries(ret.followups_by_status || {}).map(([k, v]) => ({ label: humanize(k), value: v })),
    [ret]
  );

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="fl-label text-[#f47a20]">{t("pages.aOverview.eyebrow")}</div>
          <h1 className="fl-heading text-3xl mt-1.5">{t("pages.aOverview.h1")}</h1>
          {a?.range && (
            <div className="text-sm mt-2" style={{ color: "var(--fl-muted)" }} data-testid="analytics-range-label">
              {a.range.from} → {a.range.to}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="fl-label text-[10px]">From</span>
            <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)}
              className="fl-input px-3 py-2 text-sm rounded-xl" data-testid="analytics-from-date" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="fl-label text-[10px]">To</span>
            <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)}
              className="fl-input px-3 py-2 text-sm rounded-xl" data-testid="analytics-to-date" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="fl-label text-[10px]">Clinician</span>
            <select value={clinician} onChange={(e) => setClinician(e.target.value)}
              className="fl-input px-3 py-2 text-sm rounded-xl" data-testid="analytics-clinician-select">
              <option value="">All clinicians</option>
              {clinicians.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <button onClick={() => { setFrom(""); setTo(""); setClinician(""); }}
            disabled={!(from || to || clinician)}
            className="fl-btn-ghost px-3 py-2 text-sm rounded-xl disabled:opacity-40" data-testid="analytics-reset-btn">Reset</button>
        </div>
      </div>

      {err && (
        <div className="fl-card p-5 flex items-center gap-2 text-sm" style={{ color: "var(--fl-muted)" }} data-testid="analytics-error">
          <AlertTriangle size={16} className="text-[#f47a20]" /> Couldn’t load analytics. Please try again.
        </div>
      )}

      {loading && !a && (
        <div className="fl-card p-8 text-center text-sm" style={{ color: "var(--fl-muted)" }} data-testid="analytics-loading">Loading analytics…</div>
      )}

      {a && (
        <div data-testid="analytics-dashboard">
          {/* ---- Acquisition ---- */}
          <SectionTitle>Acquisition &amp; Conversion</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={UserPlus} label="New patients" value={acq.new_patients} testid="kpi-new-patients" />
            <Kpi icon={MailCheck} label="Email verified" value={acq.verified_patients} testid="kpi-verified" />
            <Kpi icon={CalendarCheck} label="Booked" value={acq.booked_patients}
              sub={`Conversion ${pct(acq.booking_conversion_rate)}`} testid="kpi-booked" />
            <Kpi icon={CreditCard} label="Paid conversions" value={acq.paid_conversions}
              sub={`Conversion ${pct(acq.paid_conversion_rate)}`} iconCls="text-green-500" testid="kpi-paid-conversions" />
          </div>
          <div className="fl-card p-5 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-[#f47a20]" />
              <div className="fl-label">New patients per day</div>
            </div>
            <BarChart data={acq.trend} valueKey="count" labelKey="date" testid="chart-acquisition" />
          </div>

          {/* ---- Retention ---- */}
          <SectionTitle>Retention &amp; Engagement</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Activity} label="Active patients" value={ret.active_patients} testid="kpi-active-patients" />
            <Kpi icon={Repeat} label="Returning (2+ visits)" value={ret.returning_patients} testid="kpi-returning" />
            <Kpi icon={HeartPulse} label="Check-ins" value={ret.checkins} iconCls="text-[#f47a20]" testid="kpi-checkins" />
            <Kpi icon={AlertTriangle} label="Drop-off at risk" value={ret.drop_off_at_risk}
              sub={`${ret.active_programme_patients ?? 0} on active programmes`} iconCls="text-amber-500" testid="kpi-dropoff" />
          </div>
          <div className="fl-card p-5 mt-4">
            <div className="fl-label mb-3">Follow-up actions by status</div>
            <HBars rows={fuRows} testid="chart-followups" />
          </div>

          {/* ---- Revenue ---- */}
          <SectionTitle>Revenue &amp; Memberships</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={PoundSterling} label="Paid revenue" value={fmtGBP(rev.paid_revenue)}
              sub={`${rev.paid_transactions ?? 0} paid · ${rev.pending_transactions ?? 0} pending`} iconCls="text-green-500" testid="kpi-revenue" />
            <Kpi icon={Sparkles} label="Active memberships" value={rev.active_memberships} iconCls="text-[#f47a20]" testid="kpi-memberships" />
            <Kpi icon={Calendar} label="Appointments" value={a.appointments?.total} testid="kpi-appointments" />
            <Kpi icon={CreditCard} label="Pending transactions" value={rev.pending_transactions} iconCls="text-amber-500" testid="kpi-pending" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <div className="fl-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-green-500" />
                <div className="fl-label">Revenue per day</div>
              </div>
              <BarChart data={rev.trend} valueKey="amount" labelKey="date" color="#22c55e" format={fmtGBP} testid="chart-revenue" />
            </div>
            <div className="fl-card p-5">
              <div className="fl-label mb-3">Revenue by package</div>
              <HBars rows={(rev.by_package || []).map((r) => ({ label: r.package, value: r.amount }))}
                color="#22c55e" format={fmtGBP} testid="chart-revenue-package" />
            </div>
          </div>

          {/* ---- Utilisation ---- */}
          <SectionTitle>Clinician Utilisation</SectionTitle>
          <div className="fl-card p-0 overflow-x-auto" data-testid="utilisation-table">
            {util.length === 0 ? (
              <div className="p-6 text-sm text-center" style={{ color: "var(--fl-muted-2)" }}>No clinician activity in range</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ color: "var(--fl-muted)", borderBottom: "1px solid var(--fl-border)" }}>
                    <th className="px-4 py-3 font-medium">Clinician</th>
                    <th className="px-4 py-3 font-medium text-right">Appts</th>
                    <th className="px-4 py-3 font-medium text-right">Completed</th>
                    <th className="px-4 py-3 font-medium text-right">Cancelled</th>
                    <th className="px-4 py-3 font-medium text-right">Completion</th>
                    <th className="px-4 py-3 font-medium text-right">Booked hrs</th>
                    <th className="px-4 py-3 font-medium text-right">Patients</th>
                    <th className="px-4 py-3 font-medium text-right">Programmes</th>
                  </tr>
                </thead>
                <tbody>
                  {util.map((u) => (
                    <tr key={u.clinician_id} style={{ borderBottom: "1px solid var(--fl-border)" }} data-testid={`util-row-${u.clinician_id}`}>
                      <td className="px-4 py-3" style={{ color: "var(--fl-text)" }}>{u.clinician_name}</td>
                      <td className="px-4 py-3 text-right">{u.appointments}</td>
                      <td className="px-4 py-3 text-right">{u.completed}</td>
                      <td className="px-4 py-3 text-right">{u.cancelled}</td>
                      <td className="px-4 py-3 text-right">{pct(u.completion_rate)}</td>
                      <td className="px-4 py-3 text-right">{u.booked_hours}</td>
                      <td className="px-4 py-3 text-right">{u.active_patients}</td>
                      <td className="px-4 py-3 text-right">{u.active_programmes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ---- Data notes ---- */}
          {a.notes && (
            <div className="fl-card p-5 mt-6" data-testid="analytics-notes">
              <div className="flex items-center gap-2 mb-2">
                <Info size={15} style={{ color: "var(--fl-muted)" }} />
                <div className="fl-label">What isn’t tracked</div>
              </div>
              <ul className="text-xs space-y-1" style={{ color: "var(--fl-muted)" }}>
                {Object.values(a.notes).map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
